import type { OutputPresetId } from '@/content/instagram'
import type { CollageTemplateId, GradientPresetId } from '@/content/collageTemplates'
import type { AdjustmentsParams } from '@/core/document/adjustments'
import type { AspectRatio, Rect } from '@/core/geometry'
import type { FrameParams } from '@/tools/frame/params'

export type NodeId = string & { readonly __brand: 'NodeId' }
export type SourceId = string & { readonly __brand: 'SourceId' }

export const DOCUMENT_VERSION = 2 as const

export enum NodeKind {
  Image = 'image',
  Frame = 'frame',
  Collage = 'collage',
}

export const MAX_TREE_DEPTH = 4

export enum TransformKind {
  Orientation = 'orientation',
  Crop = 'crop',
  Adjustments = 'adjustments',
}

export type QuarterTurns = 0 | 1 | 2 | 3

export interface OrientationParams {
  quarterTurns: QuarterTurns
  flipHorizontal: boolean
  flipVertical: boolean
  straightenDeg: number
}

export interface CropParams {
  rect: Rect
}

export enum SplitKind {
  Carousel = 'carousel',
  Grid = 'grid',
}

export type CarouselTileCount = 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10
export type GridRowCount = 1 | 2 | 3

export type SplitSettings =
  | { kind: SplitKind.Carousel; tiles: CarouselTileCount; tileRatio: AspectRatio }
  | { kind: SplitKind.Grid; rows: GridRowCount }

export interface OutputSettings {
  presetId: OutputPresetId
  split: SplitSettings | null
  background: string
}

export interface Document {
  version: typeof DOCUMENT_VERSION
  output: OutputSettings
  root: CompositionNode
}

export interface NodeBase {
  id: NodeId
}

export interface ImageNode extends NodeBase {
  kind: NodeKind.Image
  sourceId: SourceId
  transforms: TransformStack
}

export interface FrameNode extends NodeBase {
  kind: NodeKind.Frame
  params: FrameParams
  child: CompositionNode
}

export enum CollageBackgroundKind {
  Color = 'color',
  Gradient = 'gradient',
  Image = 'image',
}

export type CollageBackground =
  | { kind: CollageBackgroundKind.Color; color: string }
  | { kind: CollageBackgroundKind.Gradient; presetId: GradientPresetId; colors: [string, string] }
  | { kind: CollageBackgroundKind.Image; sourceId: SourceId; blur: number }

export const DEFAULT_COLLAGE_BACKGROUND: Readonly<CollageBackground> = {
  kind: CollageBackgroundKind.Color,
  color: '#ffffff',
}

export const MAX_COLLAGE_GAP = 0.08
export const MAX_COLLAGE_BACKGROUND_BLUR = 40

export interface CollageNode extends NodeBase {
  kind: NodeKind.Collage
  templateId: CollageTemplateId
  gap: number
  background: CollageBackground
  cells: (CompositionNode | null)[]
  tray: CompositionNode[]
}

export interface TransformStack {
  [TransformKind.Orientation]?: OrientationParams
  [TransformKind.Crop]?: CropParams
  [TransformKind.Adjustments]?: AdjustmentsParams
}

export type CompositionNode = ImageNode | FrameNode | CollageNode
